import { Icon } from "@iconify/react";
import { motion, AnimatePresence } from "framer-motion";
import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import FeatureSectionAIDemo from "../features/FeatureSectionAIDemo";
import FeatureSectionPDF from "../features/FeatureSectionPDF";
import FeatureSectionInstantTools from "../features/FeatureSectionInstantTools";
import FeatureSectionRecording from "../features/FeatureSectionRecording";
import FeatureSectionStudyTools from "../features/FeatureSectionStudyTools";
import ConsumerWorkflowDemo from "../modern/ConsumerWorkflowDemo";
import { buildAppLink } from "../../../utils/landingRedirect";

const SLIDE_COUNT = 5;
const AUTO_ROTATE_MS = 5000; // 5s per slide

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 400 : -400,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -400 : 400,
    opacity: 0,
  }),
};
const InteractiveHero: React.FC = () => {
  const { t } = useTranslation();
  const [activeSlide, setActiveSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);

  const goToSlide = useCallback((index: number) => {
    setDirection(index > activeSlide ? 1 : -1);
    setActiveSlide(index);
  }, [activeSlide]);

  const goNext = useCallback(() => {
    setDirection(1);
    setActiveSlide((prev) => (prev + 1) % SLIDE_COUNT);
  }, []);

  const goPrev = useCallback(() => {
    setDirection(-1);
    setActiveSlide((prev) => (prev - 1 + SLIDE_COUNT) % SLIDE_COUNT);
  }, []);

  // Auto-rotate
  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(goNext, AUTO_ROTATE_MS);
    return () => clearInterval(timer);
  }, [isPaused, goNext]);

  const slides = [
    <FeatureSectionRecording key="recording" autoStart />,
    <FeatureSectionPDF key="pdf" autoStart />,
    <FeatureSectionAIDemo key="ai-demo" autoStart />,
    <FeatureSectionInstantTools key="instant-tools" showDescription={false} />,
    <FeatureSectionStudyTools key="study-tools" showDescription={false} />,
  ];

  return (
    <div className="w-full bg-white relative">
      {/* 1. HERO SECTION (Title, Text, Buttons) */}
      <section className="landing-snap-section relative z-10 flex min-h-screen items-center bg-[#fbfdff] pt-[73px]">
        <div className="container mx-auto px-4">
          <div className="mx-auto flex max-w-5xl -translate-y-4 flex-col items-center text-center md:-translate-y-6">
              <motion.div
                className="mb-6 inline-flex flex-wrap items-center justify-center gap-2 text-sm font-semibold text-cornflowerblue"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
              >
                <Icon icon="lucide:database-zap" className="text-base" />
                {t("modern.workBrain.hero.badge", "AI memory-to-action for humans and agents")}
              </motion.div>

              {/* Main Heading */}
              <motion.h1
                className="mb-5 text-[34px] font-bold leading-tight tracking-normal text-gray-950 md:text-[56px] lg:text-[72px]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                {t("modern.workBrain.hero.title", "Turn scattered context into AI action.")}
              </motion.h1>

              {/* Subtitle */}
              <motion.p
                className="mb-8 max-w-2xl text-base leading-7 text-gray-700 md:text-xl md:leading-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <span className="md:hidden">
                  {t("modern.workBrain.hero.coverSubtitleMobile", "Turn your work context into one source-backed memory layer agents can use.")}
                </span>
                <span className="hidden md:inline">
                  {t("modern.workBrain.hero.coverSubtitleDesktop", "Meetings, PDFs, recordings, email, chat, and decisions become one source-backed memory layer for agents to answer, plan, and act with your approval.")}
                </span>
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                className="flex w-full max-w-sm flex-col gap-3 sm:max-w-none sm:flex-row sm:items-center sm:justify-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                {/* Free Trial */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full sm:w-auto"
                >
                  <a
                    href={buildAppLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-cornflowerblue px-6 text-base font-semibold text-white no-underline transition-colors hover:bg-blue-500 sm:w-auto md:h-14"
	                  >
	                    <Icon icon="lucide:play" className="text-xl" />
                    {t("modern.workBrain.hero.primaryCta", "Build your first workflow")}
	                  </a>
	                </motion.div>
                <motion.a
                  href="https://apps.apple.com/app/youwo-ai-agent/id6760689478"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-gray-950 px-6 text-base font-semibold text-white no-underline transition-colors hover:bg-gray-800 sm:w-auto md:h-14"
                >
                  <Icon icon="lucide:apple" className="text-xl" />
                  {t("modern.interactiveHero.hero.downloadIos", "Download iOS")}
                </motion.a>
                <motion.a
                  href="https://downloads.youwo.ai/mac/YouWo-latest.dmg"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-6 text-base font-semibold text-gray-900 no-underline transition-colors hover:border-cornflowerblue hover:text-cornflowerblue sm:w-auto md:h-14"
                >
                  <Icon icon="lucide:monitor" className="text-xl" />
                  {t("modern.interactiveHero.hero.downloadMac", "Download Mac")}
                </motion.a>
              </motion.div>

          </div>
        </div>
      </section>

      <ConsumerWorkflowDemo />

      {/* 2. FEATURE CAROUSEL */}
      <motion.section
        className="landing-snap-section relative flex min-h-screen flex-col justify-center pt-10"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Carousel container */}
        <div className="relative overflow-hidden" style={{ minHeight: "auto" }}>
          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.div
              key={activeSlide}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="w-full"
            >
              {slides[activeSlide]}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation arrows */}
        <button
          onClick={goPrev}
          className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/80 hover:bg-white shadow-md flex items-center justify-center transition-colors border-none cursor-pointer"
        >
          <Icon icon="lucide:chevron-left" className="text-xl text-gray-700" />
        </button>
        <button
          onClick={goNext}
          className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/80 hover:bg-white shadow-md flex items-center justify-center transition-colors border-none cursor-pointer"
        >
          <Icon icon="lucide:chevron-right" className="text-xl text-gray-700" />
        </button>

        {/* Dot indicators */}
        <div className="mt-5 flex justify-center gap-2 pb-6 pt-2 md:mt-7 md:pb-5">
          {Array.from({ length: SLIDE_COUNT }).map((_, i) => (
            <button
              key={i}
              onClick={() => goToSlide(i)}
              className={`rounded-full transition-all duration-300 border-none cursor-pointer ${
                i === activeSlide
                  ? "w-8 h-2.5 bg-cornflowerblue"
                  : "w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400"
              }`}
            />
          ))}
        </div>
      </motion.section>
    </div>
  );
};

export default InteractiveHero;
